import { EvmChainId } from '@injectivelabs/ts-types';
import { toUtf8, TxGrpcApi } from '@injectivelabs/sdk-ts';
import { EthereumProvider } from '@bangjelkoski/wc-ethereum-provider';
import { WalletAction, WalletDeviceType, WalletEventListener, BaseConcreteStrategy, } from '@injectivelabs/wallet-base';
import { ErrorType, WalletException, UnspecifiedErrorCode, TransactionException, WalletConnectException, } from '@injectivelabs/exceptions';
const WalletConnectIds = {
    FireBlocks: '5864e2ced7c293ed18ac35e0db085c09ed567d67346ccb6f58a0327a75137489',
};
export class WalletConnect extends BaseConcreteStrategy {
    provider;
    async getWalletDeviceType() {
        return Promise.resolve(WalletDeviceType.Browser);
    }
    async enable(args) {
        await this.connectWalletConnect(args?.topic);
        return Promise.resolve(true);
    }
    async disconnect() {
        if (this.listeners[WalletEventListener.AccountChange]) {
            const wc = await this.getConnectedWalletConnect();
            wc.removeListener('accountsChanged', this.listeners[WalletEventListener.AccountChange]);
        }
        if (this.listeners[WalletEventListener.ChainIdChange]) {
            const wc = await this.getConnectedWalletConnect();
            wc.removeListener('chainChanged', this.listeners[WalletEventListener.ChainIdChange]);
        }
        this.listeners = {};
        if (this.provider) {
            await this.provider.disconnect();
            this.provider = undefined;
        }
    }
    async getAddresses() {
        const wc = await this.getConnectedWalletConnect();
        try {
            return await wc.request({
                method: 'eth_requestAccounts',
            });
        }
        catch (e) {
            throw new WalletConnectException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.GetAccounts,
            });
        }
    }
    async getSessionOrConfirm(_address) {
        const wc = await this.getConnectedWalletConnect();
        return wc.session?.topic || '';
    }
    async sendEvmTransaction(transaction, _options) {
        const wc = await this.getConnectedWalletConnect();
        try {
            return await wc.request({
                method: 'eth_sendTransaction',
                params: [transaction],
            });
        }
        catch (e) {
            throw new WalletConnectException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SendEvmTransaction,
            });
        }
    }
    async sendTransaction(transaction, options) {
        const { endpoints, txTimeout } = options;
        if (!endpoints) {
            throw new WalletException(new Error('You have to pass endpoints within the options for using Ethereum native wallets'));
        }
        const txApi = new TxGrpcApi(endpoints.grpc);
        const response = await txApi.broadcast(transaction, { txTimeout });
        if (response.code !== 0) {
            throw new TransactionException(new Error(response.rawLog), {
                code: UnspecifiedErrorCode,
                contextCode: response.code,
                contextModule: response.codespace,
            });
        }
        return response;
    }
    async signEip712TypedData(eip712json, address, options = {}) {
        const wc = await this.getConnectedWalletConnect();
        console.log('signEip712TypedData', options?.txTimeout);
        try {
            return await wc.request({
                method: 'eth_signTypedData_v4',
                params: [address, eip712json],
            }, options?.txTimeout || undefined);
        }
        catch (e) {
            throw new WalletConnectException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SignTransaction,
            });
        }
    }
    async signAminoCosmosTransaction(_transaction) {
        throw new WalletException(new Error('This wallet does not support signing Cosmos transactions'), {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.SignTransaction,
        });
    }
    async signCosmosTransaction(_transaction) {
        throw new WalletException(new Error('This wallet does not support signing Cosmos transactions'), {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.SignTransaction,
        });
    }
    async signArbitrary(signer, data) {
        const wc = await this.getConnectedWalletConnect();
        try {
            const signature = await wc.request({
                method: 'personal_sign',
                params: [toUtf8(data), signer],
            });
            return signature;
        }
        catch (e) {
            throw new WalletConnectException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SignArbitrary,
            });
        }
    }
    async getEthereumChainId() {
        const wc = await this.getConnectedWalletConnect();
        try {
            return wc.request({ method: 'eth_chainId' });
        }
        catch (e) {
            throw new WalletConnectException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.GetChainId,
            });
        }
    }
    async getEvmTransactionReceipt(_txHash) {
        throw new WalletException(new Error('This wallet does not support awaiting Evm transactions'), {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.GetEvmTransactionReceipt,
        });
    }
    async getPubKey() {
        throw new WalletException(new Error('You can only fetch PubKey from Cosmos native wallets'));
    }
    async onChainIdChanged(callback) {
        const wc = await this.getConnectedWalletConnect();
        this.listeners = {
            [WalletEventListener.ChainIdChange]: callback,
        };
        wc.on('chainChanged', callback);
    }
    async onAccountChange(callback) {
        const wc = await this.getConnectedWalletConnect();
        this.listeners = {
            [WalletEventListener.AccountChange]: callback,
        };
        wc.on('accountsChanged', (accounts) => callback(accounts[0]));
    }
    async getWalletConnect() {
        if (this.provider) {
            return this.provider;
        }
        if (!this.metadata?.walletConnect) {
            throw new WalletException(new Error('Please provide metadata for WalletConnect'), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.GetAccounts,
            });
        }
        if (!this.metadata.walletConnect.projectId) {
            throw new WalletException(new Error('Please provide projectId alongside the metadata for WalletConnect'), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.GetAccounts,
            });
        }
        try {
            this.provider = await EthereumProvider.init({
                projectId: this.metadata.walletConnect.projectId,
                metadata: this
                    .metadata,
                showQrModal: true,
                optionalChains: this.evmChainId
                    ? [this.evmChainId]
                    : [EvmChainId.Mainnet, EvmChainId.Sepolia],
                qrModalOptions: {
                    explorerRecommendedWalletIds: [WalletConnectIds.FireBlocks],
                    explorerExcludedWalletIds: 'ALL',
                    mobileWallets: [],
                    walletImages: {
                        [WalletConnectIds.FireBlocks]: '/wallet-connect/fireblocks.webp',
                    },
                    desktopWallets: [
                        {
                            id: WalletConnectIds.FireBlocks,
                            name: 'Fireblocks',
                            links: {
                                native: 'fireblocks-wc://',
                                universal: 'https://console.fireblocks.io/v2/',
                            },
                        },
                    ],
                },
            });
            return this.provider;
        }
        catch {
            throw new WalletException(new Error('WalletConnect not supported for this wallet'), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.GetAccounts,
            });
        }
    }
    async getEip1193Provider() {
        const provider = await this.getWalletConnect();
        return provider;
    }
    async getConnectedWalletConnect() {
        if (!this.provider) {
            await this.getWalletConnect();
        }
        if (!this.provider?.connected) {
            await this.enable();
        }
        return this.provider;
    }
    async connectWalletConnect(topic) {
        if (this.provider && this.provider.connected) {
            return;
        }
        const wc = await this.getWalletConnect();
        await wc.connect({
            ...(topic && { pairingTopic: topic }),
        });
    }
}
